import { WorkspaceLayout } from "@/components/layout";
import MappingViewPanel from "@/components/mapping";
import { useAutoTagCompletionEffect } from "@/components/mapping/auto-tag-progress/use-auto-tag-completion";
import { useForwardCompletionEffect } from "@/components/workspace/forward/use-forward-completion";
import {
  useCheckTableContentChanged,
  useCheckWorkbookId,
  useGetSelectedTable,
  useSelectedCell,
} from "@/components/mapping/hooks";
import { RoleSelectionPanel } from "@/components/mapping/roles/role-selection-panel";
import SidebarLayout from "@/components/sidebar";
import { ChangeReviewModalConnected } from "@/components/workspace/forward/change-review-modal-connected";
import { useAnalyzeXbrlOnEntry } from "@/hooks/use-analyze-xbrl-on-entry";
import { useIsWorkbookAnalyzing } from "@/hooks/use-is-workbook-analyzing";
import { useXbrlAnalysisCompletionEffect } from "@/hooks/use-xbrl-analysis-completion";
import { useWorkbookId } from "@/hooks/use-workbook";
import queries from "@/queries";
import { useMappingStore } from "@/stores/mapping";
import { useModalStore } from "@/stores/modal";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";

// 버전 포워드 업로드 직후 이 화면이 새 탭으로 열릴 때 vfReviewSrcWb(직전본 워크북) 쿼리가
// 실려 온다. trg 워크북은 URL 경로(현재 화면). 둘이 있으면 "변경 사항 확인" 모달을 한 번
// 띄우고, 새로고침 시 다시 뜨지 않도록 URL 에서 파라미터를 지운다. reviewSrc/reviewTrg 는
// "변경 내역 전체 보기"(dsd-dsd 상세 대사)로 넘길 업로드 파일 짝.
const useShowChangeReviewFromUrl = (
  trgWorkbookId: string | null | undefined,
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { push } = useModalStore((state) => state.modals);
  const shownRef = useRef(false);

  const srcWorkbookId = searchParams.get("vfReviewSrcWb");
  const srcFileId = searchParams.get("reviewSrc");
  const trgFileId = searchParams.get("reviewTrg");

  useEffect(() => {
    if (shownRef.current || !srcWorkbookId || !trgWorkbookId) return;
    shownRef.current = true;
    push(ChangeReviewModalConnected, {
      srcWorkbookId,
      trgWorkbookId,
      srcFileId: srcFileId ?? undefined,
      trgFileId: trgFileId ?? undefined,
    });
    setSearchParams(
      (prev) => {
        prev.delete("vfReviewSrcWb");
        prev.delete("reviewSrc");
        prev.delete("reviewTrg");
        return prev;
      },
      { replace: true },
    );
  }, [
    srcWorkbookId,
    trgWorkbookId,
    srcFileId,
    trgFileId,
    push,
    setSearchParams,
  ]);
};

export const MappingViewEvents = () => {
  useSelectedCell();
  const { data: workbookId } = useWorkbookId();
  const { currentTableId } = useGetSelectedTable();
  const { categoryId: currentCategoryId } = useParams<{ categoryId: string }>();
  useCheckTableContentChanged(workbookId!, currentTableId);
  useCheckWorkbookId();
  useShowChangeReviewFromUrl(workbookId);

  // role 변경 패널이 떠 있을 때 URL 의 table / category 가 바뀌면 mapping panel 로 자동 복귀.
  // URL 구조: /mapping/{workbookId}/{categoryId}?table={tableId}
  // useGetSelectedTable.currentTableId 는 Excel binding 이벤트 의존이라 트리거 누락 가능 →
  // router 가 진실의 원천인 URL params 로 트래킹.
  // viewState 자체 변경에는 반응하지 않도록 ref 로 최신 값만 참조.
  const [searchParams] = useSearchParams();
  const currentTableIdFromUrl = searchParams.get("table");
  const viewState = useMappingStore((s) => s.viewState.current);
  const setViewState = useMappingStore((s) => s.viewState.set);
  const viewStateRef = useRef(viewState);
  viewStateRef.current = viewState;

  useEffect(() => {
    if (viewStateRef.current === "ROLE_SELECTION") {
      setViewState("DEFAULT");
    }
  }, [currentTableIdFromUrl, currentCategoryId, setViewState]);

  return null;
};

const MappingSidebar = () => {
  const viewState = useMappingStore((state) => state.viewState.current);
  const setViewState = useMappingStore((state) => state.viewState.set);

  // jspreadsheet은 document에 keydown / paste 핸들러를 달아 selected cell을
  // 조작한다. 매핑 패널(MappingViewPanel/RoleSelectionPanel)의 input에 타이핑하거나
  // 붙여넣을 때 셀까지 함께 바뀌는 부작용이 있어, jspreadsheet 외부 입력 요소의
  // 이벤트는 document로 bubble 못 하게 막는다.
  //
  // - keydown: selected cell에 키 입력이 반영되는 것을 막는다.
  // - paste: jspreadsheet의 document paste 핸들러는 selected cell이 있으면
  //   클립보드를 셀에 붙여넣고 preventDefault()를 호출한다. 이 때문에 input에서
  //   Cmd/Ctrl+V가 동작하지 않는다(paste는 keydown과 별개 이벤트라 keydown 차단으로
  //   막히지 않는다).
  //
  // 부착 위치는 document가 아니라 React 18 root container("#container")이다.
  // React 18은 합성 이벤트를 document가 아니라 root에 위임하므로, document에
  // capture phase로 막으면 root까지 이벤트가 내려가지 못해 onKeyDown 같은 합성
  // 핸들러가 함께 죽는다(예: 검색 input의 Enter). root의 bubble phase에 native
  // listener를 달면 React가 합성 이벤트를 dispatch한 뒤 우리가 stopPropagation을
  // 호출하므로 React 핸들러는 정상 작동하면서 document의 jspreadsheet 핸들러로는
  // 전파되지 않는다.
  useEffect(() => {
    const root = document.getElementById("container");
    if (!root) return;
    const isExternalEditable = (target: HTMLElement | null) => {
      if (!target?.tagName) return false;
      if (target.closest?.(".jss_worksheet")) return false;
      return (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      );
    };
    const stopFromEditable = (e: Event) => {
      if (!isExternalEditable(e.target as HTMLElement | null)) return;
      e.stopPropagation();
    };

    root.addEventListener("keydown", stopFromEditable);
    root.addEventListener("paste", stopFromEditable);
    return () => {
      root.removeEventListener("keydown", stopFromEditable);
      root.removeEventListener("paste", stopFromEditable);
    };
  }, []);

  switch (viewState) {
    case "ROLE_SELECTION":
      return (
        <RoleSelectionPanel resetViewState={() => setViewState("DEFAULT")} />
      );
    case "DEFAULT":
    default:
      return <MappingViewPanel />;
  }
};

const MappingView = () => {
  const { data: workbookId } = useWorkbookId();
  const queryClient = useQueryClient();
  const clearMappingStore = useMappingStore((state) => state.clear);

  useAnalyzeXbrlOnEntry(workbookId ?? undefined);

  // 워크북 진입 시 태깅 안내를 미리 받아둔다 — 버튼이 그려지기 전에 데이터가 준비돼
  // 강조가 늦게 뜨는 깜빡임을 줄인다. staleTime:Infinity 라 표 이동 간 재요청은 없다.
  // 분석 중에는 받지 않는다 — 그때의 답(NONE)이 캐시에 굳으면 분석이 끝나도 안 뜬다.
  const isWorkbookAnalyzing = useIsWorkbookAnalyzing(workbookId ?? undefined);
  useEffect(() => {
    if (!workbookId || isWorkbookAnalyzing) return;
    queryClient.prefetchQuery({
      ...queries.workbook(workbookId)._ctx.taggingGuidance(),
      staleTime: Infinity,
    });
  }, [workbookId, isWorkbookAnalyzing, queryClient]);

  // XBRL 분석 완료 감시 — 분석 중에 들어온 경우 완료 시 버튼 활성화·태깅 안내를 갱신한다.
  useXbrlAnalysisCompletionEffect(workbookId ?? undefined);

  // 오토태깅 진행 폴링 + 완료 처리(워크북 invalidate·토스트). 사이드바 패널이
  // 아니라 페이지 루트에 둬야 오토태깅 섹션을 안 연 유저도 완료를 알 수 있다.
  useAutoTagCompletionEffect(workbookId ?? undefined);

  // 롤포워드 진행 폴링 + 완료 처리. 진행 모달을 닫아도 툴바 버튼의 진행 표시와
  // 완료 처리가 이어지도록 모달이 아니라 페이지 루트에 둔다.
  useForwardCompletionEffect(workbookId ?? undefined);

  // Workbook 이동할 때 (unmount 시) query랑 store 초기화 필요
  useEffect(() => {
    return () => {
      if (workbookId) {
        // 이 workbook에 관련된 모든 table-related queries 제거
        // DSD 이동할 때 remove 주로 실행됨.
        queryClient.removeQueries({
          queryKey: queries.workbook(workbookId)._ctx.tables._def,
        });
      }
      // mapping store 초기화
      clearMappingStore();
    };
  }, [workbookId]);

  return (
    <SidebarLayout>
      <WorkspaceLayout>
        <div className="flex flex-col min-h-screen w-full overflow-hidden">
          <div className="flex flex-row w-full overflow-hidden flex-1">
            <MappingSidebar />
            <MappingViewEvents />
          </div>
        </div>
      </WorkspaceLayout>
    </SidebarLayout>
  );
};

export default MappingView;
